import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getConnectionsScores,
  startPopulateScoresJob,
  getPopulateScoresStatus,
  getStoredScoreJobId,
  setStoredScoreJobId,
  deleteConnectionsScoreById,
  updateCompanyScore,
} from '../services/connectionsService';
import { getResumes, getJobsForResume } from '../services/resumeService';
import { ConnectionsScore } from '../types/connections';
import { JobForYou } from '../types/jobs';

const POLL_INTERVAL_MS = 30000;

const formControlClass =
  'border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400';

/** Effective display score: manual override if set, else Max Score */
function getEffectiveScore(s: ConnectionsScore): number {
  if (s.manualScoreOverride !== undefined && s.manualScoreOverride !== null) {
    return s.manualScoreOverride;
  }
  return s['Max Score'];
}

/** Normalize company name for fuzzy merging (lowercase, collapse punctuation/whitespace) */
const normalizeCompany = (name: string): string =>
  name.trim().toLowerCase().replace(/[,.\-–—'']/g, ' ').replace(/\s+/g, ' ');

type ScoreUpdatedDateFilter = 'all' | 'today' | 'last-7d' | 'last-30d' | 'missing';
type ScoreBandFilter = 'all' | '70-100' | '50-69' | '30-49' | '0-29';

const getScoreDocumentMs = (s: ConnectionsScore): number | null => {
  const raw = s.updatedAt || s.createdAt;
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * A company that may have LinkedIn connections, job matches, or both.
 * The two data sources are merged by company name (fuzzy match).
 */
interface TargetCompany {
  /** Canonical display name (prefers scored-company name when both exist) */
  company: string;
  /** Score document — undefined if no connections have been scored for this company */
  scoreDoc?: ConnectionsScore;
  /** All filtered jobs at this company (may be empty) */
  jobs: JobForYou[];
}

/**
 * Merge ConnectionsScore list with jobsByCompany map into one unified list.
 * Companies that appear in both sources are joined (fuzzy name match).
 * Companies that appear only in jobs are added with no scoreDoc.
 */
function buildMergedList(
  scores: ConnectionsScore[],
  jobsByCompany: Map<string, JobForYou[]>
): TargetCompany[] {
  // normalized key → TargetCompany
  const map = new Map<string, TargetCompany>();

  // Pass 1: all scored companies
  for (const score of scores) {
    const key = normalizeCompany(score.Company);
    map.set(key, { company: score.Company, scoreDoc: score, jobs: [] });
  }

  // Pass 2: attach jobs (or create jobs-only entries)
  for (const [jobKey, jobs] of Array.from(jobsByCompany)) {
    if (!jobs.length) continue;

    // Exact match
    if (map.has(jobKey)) {
      map.get(jobKey)!.jobs = jobs;
      continue;
    }

    // Partial match: one name contains the other
    let matched = false;
    for (const [scoreKey, tc] of Array.from(map)) {
      if (scoreKey.includes(jobKey) || jobKey.includes(scoreKey)) {
        tc.jobs = jobs;
        matched = true;
        break;
      }
    }

    if (!matched) {
      // Jobs-only company — use canonical name from the first job's sourceCompany
      const canonicalName = jobs[0].sourceCompany?.trim() || jobKey;
      map.set(jobKey, { company: canonicalName, scoreDoc: undefined, jobs });
    }
  }

  return Array.from(map.values());
}

interface CompaniesToTargetViewProps {
  /** When set, filter to connections at this company (from job deep link) */
  highlightCompany?: string | null;
}

const CompaniesToTargetView: React.FC<CompaniesToTargetViewProps> = ({ highlightCompany }) => {
  const { user } = useAuth();
  const [scores, setScores] = useState<ConnectionsScore[]>([]);
  const [jobsByCompany, setJobsByCompany] = useState<Map<string, JobForYou[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'score' | 'company' | 'contacts' | 'jobs'>('score');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScoreBand, setFilterScoreBand] = useState<ScoreBandFilter>('all');
  const [filterUpdatedDate, setFilterUpdatedDate] = useState<ScoreUpdatedDateFilter>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editScore, setEditScore] = useState<number | ''>(0);
  const [editReason, setEditReason] = useState('');
  const [rerunningScores, setRerunningScores] = useState(false);
  const [scoreMessage, setScoreMessage] = useState<string | null>(null);
  const [scoreJobMessage, setScoreJobMessage] = useState<string | null>(null);
  const [scoreJobId, setScoreJobId] = useState<string | null>(getStoredScoreJobId());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    // Reset to true on every mount/re-invoke (handles React Strict Mode double-fire)
    mountedRef.current = true;
    if (!user) {
      setLoading(false);
      return () => { mountedRef.current = false; };
    }
    loadScores();
    loadJobMatches();
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadScores/loadJobMatches are stable helpers; user?.uid is the real dep
  }, [user?.uid]);

  // Auto-expand highlighted company from deep link
  useEffect(() => {
    if (highlightCompany && mergedCompanies.length > 0) {
      const h = highlightCompany.toLowerCase();
      const match = mergedCompanies.find(
        (tc) => tc.company.toLowerCase().includes(h) || h.includes(tc.company.toLowerCase())
      );
      if (match) setExpandedId(match.scoreDoc?.id ?? match.company);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightCompany, scores, jobsByCompany]);

  // Poll for async score job
  useEffect(() => {
    if (!scoreJobId || !user) return;

    const poll = async () => {
      try {
        const token = await user.getIdToken();
        const status = await getPopulateScoresStatus(token, scoreJobId);
        setScoreJobMessage(status.message ?? status.status);
        if (status.status === 'completed') {
          const r = status.result;
          setScoreMessage(
            r
              ? `Scoring complete: ${r.documents_created} company documents from ${r.total_connections} connections.`
              : 'Scoring complete.'
          );
          setTimeout(() => setScoreMessage(null), 6000);
          setStoredScoreJobId(null);
          setScoreJobId(null);
          setScoreJobMessage(null);
          setRerunningScores(false);
          await loadScores();
        } else if (status.status === 'failed') {
          setError(status.error || 'Rerun scoring failed');
          setStoredScoreJobId(null);
          setScoreJobId(null);
          setScoreJobMessage(null);
          setRerunningScores(false);
        } else {
          pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch (err: any) {
        const msg = err?.message || 'Failed to fetch status';
        if (msg.includes('404') || msg.includes('Job not found')) {
          setError('Score job no longer found. The server may have restarted. Please try Rerun scoring again.');
        } else {
          setError(msg);
        }
        setStoredScoreJobId(null);
        setScoreJobId(null);
        setScoreJobMessage(null);
        setRerunningScores(false);
      }
    };

    if (scoreJobId) setRerunningScores(true);
    poll();
    return () => { if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current); };
  }, [scoreJobId, user]);

  const loadScores = async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);
      const token = await user.getIdToken();
      const response = await getConnectionsScores(token);
      if (mountedRef.current) setScores(response.scores);
    } catch (err: any) {
      if (mountedRef.current) setError(err.message || 'Failed to load company scores');
    } finally {
      setLoading(false); // always clear — stuck spinner is worse than extra state update
    }
  };

  const loadJobMatches = async () => {
    try {
      const resumes = await getResumes();
      if (!resumes.length) return;
      const primary = resumes.reduce(
        (best, r) => ((r['filtered-jobs-count'] ?? 0) > (best['filtered-jobs-count'] ?? 0) ? r : best),
        resumes[0]
      );
      if (!primary?.id) return;

      const jobs = await getJobsForResume(primary.id);
      const map = new Map<string, JobForYou[]>();
      for (const job of jobs) {
        const company = (job.sourceCompany || '').trim();
        if (!company) continue;
        const key = normalizeCompany(company);
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(job);
      }
      if (mountedRef.current) setJobsByCompany(map);
    } catch {
      // Silently fail — job data is an enhancement, not critical
    }
  };

  /** Unified list of all companies from both data sources */
  const mergedCompanies = useMemo(
    () => buildMergedList(scores, jobsByCompany),
    [scores, jobsByCompany]
  );

  const handleRerunScoring = async () => {
    if (!user) return;
    try {
      setRerunningScores(true);
      setError(null);
      setScoreMessage(null);
      const token = await user.getIdToken();
      const { jobId } = await startPopulateScoresJob(token);
      setStoredScoreJobId(jobId);
      setScoreJobId(jobId);
    } catch (err: any) {
      setError(err.message || 'Failed to start rerun scoring');
      setRerunningScores(false);
    }
  };

  const handleDeleteScore = async (tc: TargetCompany) => {
    const id = tc.scoreDoc?.id;
    if (!id || !user) return;
    if (!window.confirm(`Remove "${tc.company}" from your company scores?`)) return;
    try {
      setDeletingId(id);
      setError(null);
      const token = await user.getIdToken();
      await deleteConnectionsScoreById(token, id);
      setScores((prev) => prev.filter((s) => s.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete company score');
    } finally {
      setDeletingId(null);
    }
  };

  const handleStartEdit = (tc: TargetCompany) => {
    if (!tc.scoreDoc) return;
    setEditingId(tc.scoreDoc.id ?? null);
    setEditScore(tc.scoreDoc.manualScoreOverride ?? getEffectiveScore(tc.scoreDoc));
    setEditReason(tc.scoreDoc.manualScoreReason ?? '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditScore(0);
    setEditReason('');
  };

  const handleSaveScore = async (tc: TargetCompany) => {
    const id = tc.scoreDoc?.id;
    if (!id || !user) return;
    const override = editScore === '' ? null : Math.min(100, Math.max(0, Number(editScore)));
    const reason = editReason.trim() || null;
    try {
      setError(null);
      const token = await user.getIdToken();
      await updateCompanyScore(token, id, { manualScoreOverride: override, manualScoreReason: reason });
      setScores((prev) =>
        prev.map((s) =>
          s.id === id
            ? { ...s, manualScoreOverride: override ?? undefined, manualScoreReason: reason ?? undefined }
            : s
        )
      );
      handleCancelEdit();
    } catch (err: any) {
      setError(err.message || 'Failed to update score');
    }
  };

  const sortedCompanies = useMemo(() => {
    return [...mergedCompanies].sort((a, b) => {
      const aScore = a.scoreDoc ? getEffectiveScore(a.scoreDoc) : -1;
      const bScore = b.scoreDoc ? getEffectiveScore(b.scoreDoc) : -1;
      const aJobs = a.jobs.length;
      const bJobs = b.jobs.length;
      const aContacts = a.scoreDoc?.Contacts?.length ?? 0;
      const bContacts = b.scoreDoc?.Contacts?.length ?? 0;

      switch (sortBy) {
        case 'score': {
          if (bScore !== aScore) return bScore - aScore;
          if (bContacts !== aContacts) return bContacts - aContacts; // secondary: most connections
          return bJobs - aJobs;
        }
        case 'company': {
          const cmp = a.company.localeCompare(b.company);
          if (cmp !== 0) return cmp;
          return bContacts - aContacts; // secondary: most connections
        }
        case 'contacts':
          if (bContacts !== aContacts) return bContacts - aContacts;
          return bScore - aScore; // secondary when sorting by contacts: score
        case 'jobs':
          if (bJobs !== aJobs) return bJobs - aJobs;
          if (bContacts !== aContacts) return bContacts - aContacts; // secondary: most connections
          return bScore - aScore;
        default:
          return 0;
      }
    });
  }, [mergedCompanies, sortBy]);

  const displayCompanies = useMemo(() => {
    return sortedCompanies.filter((tc) => {
      if (highlightCompany) {
        const h = highlightCompany.toLowerCase();
        const c = tc.company.toLowerCase();
        if (!c.includes(h) && !h.includes(c)) return false;
      }

      const q = searchTerm.trim().toLowerCase();
      if (q) {
        const companyMatch = tc.company.toLowerCase().includes(q);
        const reasonMatch = (tc.scoreDoc?.manualScoreReason || '').toLowerCase().includes(q);
        const contactMatch = (tc.scoreDoc?.Contacts || []).some(
          (contact) => (contact['Full Name'] || '').toLowerCase().includes(q)
        );
        if (!companyMatch && !reasonMatch && !contactMatch) return false;
      }

      // Score band and date filters only apply to companies with score docs
      if (filterScoreBand !== 'all') {
        if (!tc.scoreDoc) return false; // hide unscored companies when filtering by band
        const eff = getEffectiveScore(tc.scoreDoc);
        if (filterScoreBand === '70-100' && !(eff >= 70)) return false;
        if (filterScoreBand === '50-69' && !(eff >= 50 && eff < 70)) return false;
        if (filterScoreBand === '30-49' && !(eff >= 30 && eff < 50)) return false;
        if (filterScoreBand === '0-29' && !(eff < 30)) return false;
      }

      if (filterUpdatedDate !== 'all' || customFrom || customTo) {
        if (!tc.scoreDoc) return false; // hide unscored when date-filtering
        const docMs = getScoreDocumentMs(tc.scoreDoc);
        const now = Date.now();
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        let matchesDate = true;
        if (filterUpdatedDate === 'today') matchesDate = docMs !== null && docMs >= startOfToday.getTime();
        else if (filterUpdatedDate === 'last-7d') matchesDate = docMs !== null && docMs >= now - 7 * 24 * 60 * 60 * 1000;
        else if (filterUpdatedDate === 'last-30d') matchesDate = docMs !== null && docMs >= now - 30 * 24 * 60 * 60 * 1000;
        else if (filterUpdatedDate === 'missing') matchesDate = docMs === null;
        if (customFrom) {
          const fromMs = new Date(`${customFrom}T00:00:00`).getTime();
          if (!Number.isNaN(fromMs)) matchesDate = matchesDate && docMs !== null && docMs >= fromMs;
        }
        if (customTo) {
          const toMs = new Date(`${customTo}T23:59:59`).getTime();
          if (!Number.isNaN(toMs)) matchesDate = matchesDate && docMs !== null && docMs <= toMs;
        }
        if (!matchesDate) return false;
      }

      return true;
    });
  }, [sortedCompanies, highlightCompany, searchTerm, filterScoreBand, filterUpdatedDate, customFrom, customTo]);

  const hasActiveFilters =
    !!searchTerm.trim() ||
    filterScoreBand !== 'all' ||
    filterUpdatedDate !== 'all' ||
    !!customFrom ||
    !!customTo;

  const clearFilters = () => {
    setSearchTerm('');
    setFilterScoreBand('all');
    setFilterUpdatedDate('all');
    setCustomFrom('');
    setCustomTo('');
  };

  const getScoreColor = (score: number) => {
    if (score >= 70)
      return 'text-green-700 dark:text-green-200 bg-green-100 dark:bg-green-950/60 border border-green-200/80 dark:border-green-800/60';
    if (score >= 50)
      return 'text-blue-700 dark:text-blue-200 bg-blue-100 dark:bg-blue-950/60 border border-blue-200/80 dark:border-blue-800/60';
    if (score >= 30)
      return 'text-yellow-800 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-950/50 border border-yellow-200/80 dark:border-yellow-800/50';
    return 'text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-600';
  };

  const getScoreBadgeColor = (score: number) => {
    if (score >= 70)
      return 'bg-green-100 dark:bg-green-950/60 text-green-700 dark:text-green-200 border-2 border-green-300 dark:border-green-700';
    if (score >= 50)
      return 'bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-200 border-2 border-blue-300 dark:border-blue-700';
    if (score >= 30)
      return 'bg-yellow-100 dark:bg-yellow-950/50 text-yellow-800 dark:text-yellow-200 border-2 border-yellow-300 dark:border-yellow-700';
    return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-2 border-gray-300 dark:border-gray-600';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-300">Loading companies...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  const totalCompanies = mergedCompanies.length;
  const totalScored = mergedCompanies.filter((tc) => !!tc.scoreDoc).length;
  const totalJobsOnly = totalCompanies - totalScored;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Companies to Target</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            {totalScored} companies with connections · {totalJobsOnly} from job matches only
          </p>
        </div>
        <button
          onClick={handleRerunScoring}
          disabled={rerunningScores}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {rerunningScores ? 'Rerunning scoring…' : 'Rerun scoring'}
        </button>
      </div>

      {rerunningScores && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 px-4 py-3 rounded-lg">
          {scoreJobMessage || 'Rerunning scoring...'} You can leave or reload this page.
        </div>
      )}
      {scoreMessage && !rerunningScores && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 px-4 py-3 rounded-lg">
          {scoreMessage}
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Company, contact, or override reason..."
              className={`w-full px-3 py-2 ${formControlClass}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Score range</label>
            <select
              value={filterScoreBand}
              onChange={(e) => setFilterScoreBand(e.target.value as ScoreBandFilter)}
              className={`w-full pr-8 px-3 py-2 ${formControlClass}`}
            >
              <option value="all">All scores</option>
              <option value="70-100">Strong (70–100)</option>
              <option value="50-69">Good (50–69)</option>
              <option value="30-49">Moderate (30–49)</option>
              <option value="0-29">Lower (0–29)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Score updated</label>
            <select
              value={filterUpdatedDate}
              onChange={(e) => setFilterUpdatedDate(e.target.value as ScoreUpdatedDateFilter)}
              className={`w-full pr-8 px-3 py-2 ${formControlClass}`}
            >
              <option value="all">Any time</option>
              <option value="today">Today</option>
              <option value="last-7d">Last 7 days</option>
              <option value="last-30d">Last 30 days</option>
              <option value="missing">No timestamp</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Updated from</label>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className={`w-full px-3 py-2 ${formControlClass}`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Updated to</label>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className={`w-full px-3 py-2 ${formControlClass}`} />
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className={`w-full md:w-auto min-w-[14rem] pr-8 px-3 py-2 ${formControlClass}`}
            >
              <option value="score">Connection Score (Highest First)</option>
              <option value="jobs">Job Matches (Most First)</option>
              <option value="company">Company Name (A–Z)</option>
              <option value="contacts">Number of Connections</option>
            </select>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 pb-2">
            Showing {displayCompanies.length} of {totalCompanies} companies
            {highlightCompany ? ` (narrowed from job: "${highlightCompany}")` : ''}
          </p>
          {hasActiveFilters && (
            <button type="button" onClick={clearFilters}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Deep link notice */}
      {highlightCompany && displayCompanies.length > 0 && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg px-4 py-3">
          <p className="text-sm text-purple-800 dark:text-purple-200">
            Showing companies matching <strong>{highlightCompany}</strong>
          </p>
        </div>
      )}

      {/* Companies Grid */}
      {totalCompanies === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            No companies found. Upload your LinkedIn connections CSV and ensure a resume with processed jobs is set up.
          </p>
        </div>
      ) : displayCompanies.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-600 dark:text-gray-300 mb-2">
            {hasActiveFilters ? 'No companies match your current filters.' : 'No companies to show.'}
          </p>
          {hasActiveFilters && (
            <button type="button" onClick={clearFilters}
              className="mt-4 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-3">
          {displayCompanies.map((tc) => {
            const cardId = tc.scoreDoc?.id ?? tc.company;
            const isExpanded = expandedId === cardId;
            const effective = tc.scoreDoc ? getEffectiveScore(tc.scoreDoc) : null;
            const highJobs = tc.jobs.filter((j) => j.jobFit === 'high');
            const medJobs = tc.jobs.filter((j) => j.jobFit === 'medium');
            const totalJobs = tc.jobs.length;
            const contactCount = tc.scoreDoc?.Contacts?.length ?? 0;

            if (isExpanded) {
              return (
                <div key={cardId} className="col-span-full bg-white dark:bg-gray-800 rounded-xl shadow-md border-2 border-ts-gold p-6">
                  {/* Expanded header */}
                  <div className="flex justify-between items-start gap-4 mb-4">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tc.company}</h2>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        {effective !== null ? (
                          <span className={`inline-flex items-center px-3 py-1 rounded-full font-bold text-sm ${getScoreColor(effective)}`}>
                            Score: {effective}/100
                            {tc.scoreDoc?.manualScoreOverride != null && (
                              <span className="ml-1 text-xs font-normal opacity-90">(override)</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400 italic">No connection score</span>
                        )}
                        {contactCount > 0 && (
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {contactCount} connection{contactCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        {highJobs.length > 0 && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-950/60 text-green-700 dark:text-green-200">
                            {highJobs.length} high fit
                          </span>
                        )}
                      </div>
                      {tc.scoreDoc?.manualScoreReason && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Override reason: "{tc.scoreDoc.manualScoreReason}"
                        </p>
                      )}
                    </div>

                    {/* Action buttons + close */}
                    <div className="flex items-start gap-2 flex-shrink-0 flex-wrap justify-end">
                      {tc.scoreDoc?.id && (
                        <>
                          {editingId === tc.scoreDoc.id ? (
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Score (0–100)</label>
                                <input type="number" min={0} max={100} value={editScore}
                                  onChange={(e) => setEditScore(e.target.value === '' ? '' : Number(e.target.value))}
                                  className={`w-20 px-2 py-1.5 text-sm ${formControlClass}`} />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Reason (optional)</label>
                                <input type="text" value={editReason} onChange={(e) => setEditReason(e.target.value)}
                                  placeholder="e.g. Dislike hiring process"
                                  className={`w-44 px-2 py-1.5 text-sm ${formControlClass}`} />
                              </div>
                              <div className="flex gap-2">
                                <button type="button" onClick={() => handleSaveScore(tc)}
                                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
                                <button type="button" onClick={handleCancelEdit}
                                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <button type="button" onClick={() => handleStartEdit(tc)}
                              className="px-3 py-1.5 text-sm border border-blue-300 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-50 dark:bg-blue-900/20">
                              Edit score
                            </button>
                          )}
                          <button type="button" onClick={() => handleDeleteScore(tc)}
                            disabled={deletingId === tc.scoreDoc.id}
                            className="px-3 py-1.5 text-sm border border-red-300 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-50 dark:bg-red-900/20 disabled:opacity-50">
                            {deletingId === tc.scoreDoc.id ? 'Removing...' : 'Remove'}
                          </button>
                        </>
                      )}
                      <button type="button"
                        onClick={() => { setExpandedId(null); handleCancelEdit(); }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="Close" aria-label="Close expanded view">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Contacts (if any) */}
                  {contactCount > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                        Your Connections (current or past role):
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[...(tc.scoreDoc?.Contacts ?? [])]
                          .sort((a, b) => (b['Connection Score'] ?? 0) - (a['Connection Score'] ?? 0))
                          .map((contact, idx) => (
                            <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-900/70 rounded-lg border border-gray-100 dark:border-gray-700">
                              {contact['LinkedIn URL'] ? (
                                <a
                                  href={contact['LinkedIn URL']}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-900 dark:text-gray-100 font-medium hover:text-ts-gold dark:hover:text-ts-gold hover:underline"
                                >
                                  {contact['Full Name']}
                                </a>
                              ) : (
                                <span className="text-gray-900 dark:text-gray-100 font-medium">{contact['Full Name']}</span>
                              )}
                              <span className={`px-2 py-1 text-xs font-medium rounded ${getScoreColor(contact['Connection Score'])}`}>
                                {contact['Connection Score']}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Job matches (if any) */}
                  {totalJobs > 0 && (
                    <div className={`${contactCount > 0 ? 'mt-4 ' : ''}border-t border-gray-200 dark:border-gray-600 pt-4`}>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                        Matched Jobs ({totalJobs} total)
                      </h3>
                      <div className="space-y-4">
                        {highJobs.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wide mb-2">High Fit</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {highJobs.map((j) => (
                                <div key={j.id} className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-100 dark:border-green-900/50">
                                  <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                                  {j.posting ? (
                                    <a href={j.posting} target="_blank" rel="noopener noreferrer"
                                      className="text-sm text-green-900 dark:text-green-100 hover:underline truncate">
                                      {j.postingTitle || 'View posting'}
                                    </a>
                                  ) : (
                                    <span className="text-sm text-green-900 dark:text-green-100 truncate">{j.postingTitle || '(no title)'}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {medJobs.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-2">Medium Fit</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {medJobs.map((j) => (
                                <div key={j.id} className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-100 dark:border-blue-900/50">
                                  <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                                  {j.posting ? (
                                    <a href={j.posting} target="_blank" rel="noopener noreferrer"
                                      className="text-sm text-blue-900 dark:text-blue-100 hover:underline truncate">
                                      {j.postingTitle || 'View posting'}
                                    </a>
                                  ) : (
                                    <span className="text-sm text-blue-900 dark:text-blue-100 truncate">{j.postingTitle || '(no title)'}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {contactCount === 0 && totalJobs === 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-4 text-sm text-gray-500 dark:text-gray-400">
                      No connection or job data available.
                    </div>
                  )}
                </div>
              );
            }

            // ── Compact card ──────────────────────────────────────────────
            return (
              <div key={cardId} onClick={() => setExpandedId(cardId)}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-3 cursor-pointer hover:shadow-md hover:border-ts-gold dark:hover:border-ts-gold transition-all duration-150 flex items-center gap-4">

                {/* Score badge or no-connection placeholder */}
                <div className="flex-shrink-0">
                  {effective !== null ? (
                    <div className={`w-14 h-14 rounded-full flex flex-col items-center justify-center font-bold text-xl leading-none ${getScoreBadgeColor(effective)}`}>
                      <span>{effective}</span>
                      <span className="text-[10px] font-normal opacity-70 mt-0.5">/100</span>
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-full flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-500">
                      <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.073a2.25 2.25 0 0 1-2.25 2.25h-12a2.25 2.25 0 0 1-2.25-2.25V6a2.25 2.25 0 0 1 2.25-2.25h4.073M15 3h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5 font-medium">Jobs only</span>
                    </div>
                  )}
                </div>

                {/* Right: name + stats */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm line-clamp-2 mb-2">
                    {tc.company}
                    {tc.scoreDoc?.manualScoreOverride != null && (
                      <span className="block text-[10px] text-gray-400 font-normal mt-0.5">(override)</span>
                    )}
                  </h3>

                  {/* Stats row */}
                  <div className="flex gap-1.5">
                    <div className="flex-1 bg-gray-50 dark:bg-gray-900/60 rounded-lg px-2 py-1.5 text-center">
                      <div className="font-bold text-gray-800 dark:text-gray-200 text-sm leading-none">
                        {contactCount > 0 ? contactCount : '—'}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Connections</div>
                    </div>
                    <div className="flex-1 bg-gray-50 dark:bg-gray-900/60 rounded-lg px-2 py-1.5 text-center">
                      <div className="font-bold text-gray-800 dark:text-gray-200 text-sm leading-none">
                        {totalJobs > 0 ? totalJobs : '—'}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Jobs</div>
                    </div>
                    <div className="flex-1 bg-green-50 dark:bg-green-950/30 rounded-lg px-2 py-1.5 text-center">
                      <div className="font-bold text-green-700 dark:text-green-300 text-sm leading-none">
                        {highJobs.length > 0 ? highJobs.length : '—'}
                      </div>
                      <div className="text-[10px] text-green-600 dark:text-green-400 mt-0.5">High</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info Box */}
      {totalCompanies > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">Understanding this view</h3>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
            <li>Companies with a <strong>score badge</strong> are ones where you have LinkedIn connections (scored 0–100)</li>
            <li>Companies with a <strong>briefcase icon</strong> have job matches but no scored connections</li>
            <li><strong>70–100:</strong> Strong connections · <strong>50–69:</strong> Good · <strong>30–49:</strong> Moderate · <strong>0–29:</strong> Lower priority</li>
          </ul>
          <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
            Connection scores reflect professional tier (management/executive roles) and cohort alumni tier (shared education).
            Job stats come from positions matched to your resume across all tracked job boards.
          </p>
        </div>
      )}
    </div>
  );
};

export default CompaniesToTargetView;